import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, GoogleAuthProvider, signInWithPopup, signOut, signInWithEmailAndPassword, sendPasswordResetEmail, updatePassword as firebaseUpdatePassword } from 'firebase/auth';
import { doc, setDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { AdminUser } from '../types/admin';
import { AdminService } from '../services/adminService';
import { permissionService } from '../services/permissionService';

interface AuthContextType {
  currentUser: User | null;
  adminUser: AdminUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  // roleKey is the RBAC role key used to look up permissions in
  // organizations/{orgId}/roles/{roleKey}. Admin users are not org-scoped
  // and have no roleKey in the RBAC system; this is null for all admin users.
  roleKey: null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const checkUserAuthorization = async (email: string): Promise<{ isAuthorized: boolean; role: 'super-admin' | 'admin' | null }> => {
  try {
    return await AdminService.isAuthorizedAdmin(email);
  } catch (error) {
    console.error('Authorization check failed:', error);
    return { isAuthorized: false, role: null };
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const { isAuthorized } = await checkUserAuthorization(user.email || '');
    if (!isAuthorized) {
      await signOut(auth);
      const err = new Error('This account is not authorized to access the admin panel.') as Error & { code: string };
      err.code = 'admin/not-authorized';
      throw err;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;

    const { isAuthorized } = await checkUserAuthorization(user.email || '');
    if (!isAuthorized) {
      await signOut(auth);
      const err = new Error('This account is not authorized to access the admin panel.') as Error & { code: string };
      err.code = 'admin/not-authorized';
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    try {
      await firebaseUpdatePassword(currentUser, newPassword);
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      permissionService.invalidate();
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const fetchAdminUser = async (user: User) => {
    try {
      // Single query — no duplicate read needed since onAuthStateChanged already checked authorization
      const adminUsersQuery = query(
        collection(db, 'admin-users'),
        where('email', '==', (user.email || '').toLowerCase())
      );
      const adminUsersSnapshot = await getDocs(adminUsersQuery);

      if (!adminUsersSnapshot.empty) {
        const adminDoc = adminUsersSnapshot.docs[0];
        const existingAdminUser = adminDoc.data() as AdminUser;

        // Set adminUser state FIRST so the UI isn't blocked
        const updatedAdmin = {
          ...existingAdminUser,
          id: adminDoc.id,
          uid: user.uid,
          displayName: user.displayName || existingAdminUser.displayName,
          photoURL: user.photoURL || existingAdminUser.photoURL,
          lastLogin: new Date().toISOString()
        };
        setAdminUser(updatedAdmin);

        // Re-key doc to UID if needed, then update (non-blocking)
        if (adminDoc.id !== user.uid) {
          // Doc is not keyed by UID — re-key it so Firestore rules work
          const data = { ...existingAdminUser, uid: user.uid, displayName: user.displayName || existingAdminUser.displayName, photoURL: user.photoURL || existingAdminUser.photoURL, lastLogin: serverTimestamp() };
          setDoc(doc(db, 'admin-users', user.uid), data)
            .then(() => deleteDoc(doc(db, 'admin-users', adminDoc.id)))
            .catch(err => console.warn('Re-key failed:', err.message));
        } else {
          setDoc(doc(db, 'admin-users', adminDoc.id), {
            uid: user.uid,
            displayName: user.displayName || existingAdminUser.displayName,
            photoURL: user.photoURL || existingAdminUser.photoURL,
            lastLogin: serverTimestamp()
          }, { merge: true }).catch(err => console.warn('Failed to update admin doc:', err.message));
        }
      } else {
        console.error('[fetchAdminUser] No admin user found for authorized email:', user.email);
      }
    } catch (error) {
      console.error('[fetchAdminUser] Error:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Skip authorization during invitation acceptance flow
        const invitationInProgress = localStorage.getItem('adminInvitationInProgress');
        if (invitationInProgress === 'true') {
          setCurrentUser(user);
          setLoading(false);
          return;
        }

        const { isAuthorized } = await checkUserAuthorization(user.email || '');

        if (isAuthorized) {
          setCurrentUser(user);
          await fetchAdminUser(user);
        } else {
          setCurrentUser(null);
          setAdminUser(null);
        }
      } else {
        setCurrentUser(null);
        setAdminUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    adminUser,
    loading,
    signInWithGoogle,
    signInWithEmail,
    resetPassword,
    updatePassword,
    logout,
    isAdmin: !!adminUser,
    // Admin users are not org-scoped; no RBAC roleKey applies.
    roleKey: null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};